import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usersApi, uploadApi, galleryApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { Camera, Facebook, Instagram, LayoutDashboard, ChevronLeft, Bell, BellOff, Eye, Upload, X, ImageIcon } from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { getStates, getCities, DAYS, MONTHS, YEARS, GENDERS } from '../lib/geoData';

const Profile = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { t } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { permission, isSubscribed, subscribe, unsubscribe } = usePushNotifications();
  const [form, setForm] = useState({
    name: '',
    bio: '',
    profile_image: '',
    phone: '',
    birth_day: '',
    birth_month: '',
    birth_year: '',
    gender: '',
    country: 'México',
    state: '',
    city: '',
    refund_clabe: '',
    refund_clabe_holder: '',
    refund_paypal: '',
    social_links: {
      instagram: '',
      facebook: '',
      tiktok: '',
      youtube: ''
    }
  });
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        bio: user.bio || '',
        profile_image: user.profile_image || '',
        phone: user.phone || '',
        birth_day: user.birth_day || '',
        birth_month: user.birth_month || '',
        birth_year: user.birth_year || '',
        gender: user.gender || '',
        country: 'México',
        state: user.state || '',
        city: user.city || '',
        refund_clabe: user.refund_clabe || '',
        refund_clabe_holder: user.refund_clabe_holder || '',
        refund_paypal: user.refund_paypal || '',
        social_links: {
          instagram: user.social_links?.instagram || '',
          facebook: user.social_links?.facebook || '',
          tiktok: user.social_links?.tiktok || '',
          youtube: user.social_links?.youtube || ''
        }
      });
      if (user.role === 'creator') {
        galleryApi.get(user.user_id).then(res => setGalleryPhotos(res.data.manual || [])).catch(() => {});
      }
    }
  }, [user]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadApi.uploadFile(file);
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${result.url}`;
      setForm(prev => ({ ...prev, profile_image: imageUrl }));
      toast.success('Imagen actualizada');
    } catch (error) {
      toast.error('Error al subir imagen');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!form.name?.trim()) { toast.error('El nombre es requerido *'); setLoading(false); return; }
      if (!form.birth_day || !form.birth_month || !form.birth_year) { toast.error('La fecha de nacimiento es requerida *'); setLoading(false); return; }
      if (!form.state) { toast.error('El estado es requerido *'); setLoading(false); return; }
      if (!form.bio?.trim()) { toast.error('La biografía es requerida *'); setLoading(false); return; }
      if (user?.role !== 'creator') {
        if (!form.refund_clabe && !form.refund_paypal) { toast.error('Llena al menos un método de reembolso *'); setLoading(false); return; }
        if (form.refund_clabe && form.refund_clabe.length !== 18) { toast.error('La CLABE debe tener 18 dígitos *'); setLoading(false); return; }
        if (form.refund_clabe && !form.refund_clabe_holder?.trim()) { toast.error('El nombre del titular es requerido *'); setLoading(false); return; }
      }
      const submitData = { ...form, city: form.city === '__other__' ? '' : form.city };
      const response = await usersApi.updateProfile(submitData);
      updateUser(response.data);
      toast.success('Perfil actualizado');
    } catch (error) {
      toast.error(t('msg.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (galleryPhotos.length >= 30) { toast.error('Máximo 30 fotos en la galería'); return; }
    setUploadingGallery(true);
    try {
      const result = await uploadApi.uploadFile(file);
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${result.url}`;
      const res = await galleryApi.add({ image_url: imageUrl });
      setGalleryPhotos(prev => [res.data, ...prev]);
      toast.success('Foto agregada');
    } catch (error) { toast.error('Error al subir foto'); }
    finally { setUploadingGallery(false); }
  };

  const handleGalleryDelete = async (photoId) => {
    try {
      await galleryApi.remove(photoId);
      setGalleryPhotos(prev => prev.filter(p => p.photo_id !== photoId));
      toast.success('Foto eliminada');
    } catch (error) { toast.error('Error al eliminar'); }
  };


  const handleBecomeCreator = async () => {
    try {
      await usersApi.updateRole('creator');
      updateUser({ ...user, role: 'creator' });
      toast.success('¡Ahora eres creador!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(t('msg.error'));
    }
  };

  return (
    <div className="min-h-screen pb-24 md:pb-8 bg-white">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100 safe-top">
        <div className="max-w-lg mx-auto flex items-center justify-between px-4 py-3">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-full hover:bg-gray-100"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="font-rubik font-semibold">{t('profile.edit')}</h1>
          <div className="w-9" />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 md:px-6 py-6">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Profile Image */}
          <div className="flex justify-center">
            <div className="relative">
              <Avatar className="h-28 w-28 border-4 border-white shadow-lg">
                <AvatarImage src={form.profile_image} />
                <AvatarFallback className="bg-primary text-white text-3xl font-rubik">
                  {form.name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <label className="absolute bottom-0 right-0 p-2.5 bg-white rounded-full border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors shadow-sm">
                {uploading ? (
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera className="w-5 h-5 text-gray-500" />
                )}
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              </label>
            </div>
          </div>

          {/* Name */}
          <div>
            <Label htmlFor="name" className="text-sm font-medium text-gray-500">{t('auth.name')} *</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              className="mt-2 input-airbnb"
              data-testid="profile-name-input"
            />
          </div>

          {/* Email (readonly) */}
          <div>
            <Label className="text-sm font-medium text-gray-500">{t('auth.email')}</Label>
            <Input
              value={user?.email || ''}
              disabled
              className="mt-2 input-airbnb bg-gray-50 text-gray-400"
            />
          </div>

          {/* Phone - optional */}
          <div>
            <Label htmlFor="phone" className="text-sm font-medium text-gray-500">Celular <span className="font-normal text-gray-300">· Opcional</span></Label>
            <Input
              id="phone"
              value={form.phone}
              onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
              className="mt-2 input-airbnb"
              placeholder="+52"
              data-testid="profile-phone-input"
            />
          </div>

          {/* Date of Birth */}
          <div>
            <Label className="text-sm font-medium text-gray-500">Fecha de nacimiento *</Label>
            <div className="grid grid-cols-3 gap-3 mt-2">
              <select
                value={form.birth_day}
                onChange={(e) => setForm(prev => ({ ...prev, birth_day: e.target.value ? Number(e.target.value) : '' }))}
                className="h-11 rounded-lg border border-input bg-background pl-3 pr-10 text-sm select-styled"
                data-testid="profile-birth-day"
              >
                <option value="">Día</option>
                {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <select
                value={form.birth_month}
                onChange={(e) => setForm(prev => ({ ...prev, birth_month: e.target.value ? Number(e.target.value) : '' }))}
                className="h-11 rounded-lg border border-input bg-background pl-3 pr-10 text-sm select-styled"
                data-testid="profile-birth-month"
              >
                <option value="">Mes</option>
                {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <select
                value={form.birth_year}
                onChange={(e) => setForm(prev => ({ ...prev, birth_year: e.target.value ? Number(e.target.value) : '' }))}
                className="h-11 rounded-lg border border-input bg-background pl-3 pr-10 text-sm select-styled"
                data-testid="profile-birth-year"
              >
                <option value="">Año</option>
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          {/* Gender - optional */}
          <div>
            <Label className="text-sm font-medium text-gray-500">Género <span className="font-normal text-gray-300">· Opcional</span></Label>
            <select
              value={form.gender}
              onChange={(e) => setForm(prev => ({ ...prev, gender: e.target.value }))}
              className="mt-2 w-full h-11 rounded-lg border border-input bg-background pl-3 pr-10 text-sm select-styled"
              data-testid="profile-gender"
            >
              <option value="">Seleccionar</option>
              {GENDERS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
            <p className="text-xs text-gray-300 mt-1">Usado solo para estadísticas.</p>
          </div>

          {/* Location - México fixed */}
          <div>
            <Label className="text-sm font-medium text-gray-500">Ubicación *</Label>
            <div className="space-y-3 mt-2">
              <div className="w-full h-11 rounded-lg border border-input bg-gray-50 px-3 flex items-center text-sm text-gray-500">
                México
              </div>
              <select
                value={form.state}
                onChange={(e) => setForm(prev => ({ ...prev, state: e.target.value, city: '' }))}
                className="w-full h-11 rounded-lg border border-input bg-background pl-3 pr-10 text-sm select-styled"
                data-testid="profile-state"
              >
                <option value="">Estado</option>
                {getStates('México').map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              {form.state && (() => {
                const cities = getCities('México', form.state);
                const cityInList = cities.includes(form.city);
                const showInput = form.city === '__other__' || (form.city && !cityInList);
                return (
                  <>
                    <select
                      value={cityInList ? form.city : (form.city ? '__other__' : '')}
                      onChange={(e) => setForm(prev => ({ ...prev, city: e.target.value }))}
                      className="w-full h-11 rounded-lg border border-input bg-background pl-3 pr-10 text-sm select-styled"
                      data-testid="profile-city"
                    >
                      <option value="">Ciudad</option>
                      {cities.map(c => <option key={c} value={c}>{c}</option>)}
                      <option value="__other__">Otra ciudad...</option>
                    </select>
                    {showInput && (
                      <Input
                        value={form.city === '__other__' ? '' : form.city}
                        onChange={(e) => setForm(prev => ({ ...prev, city: e.target.value || '__other__' }))}
                        placeholder="Escribe el nombre de tu ciudad"
                        className="input-airbnb"
                        autoFocus
                        data-testid="profile-city-custom"
                      />
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {/* Bio */}
          <div>
            <Label htmlFor="bio" className="text-sm font-medium text-gray-500">{t('profile.bio')} *</Label>
            <Textarea
              id="bio"
              value={form.bio}
              onChange={(e) => { if (e.target.value.length <= 500) setForm(prev => ({ ...prev, bio: e.target.value })); }}
              maxLength={500}
              rows={4}
              className="mt-2 input-airbnb resize-none"
              placeholder="Comparte quién eres, qué haces y todo lo que quieres que tu usuario conozca de ti."
              data-testid="profile-bio-input"
            />
            <p className={`text-xs mt-1 text-right ${form.bio.length >= 500 ? 'text-amber-500' : 'text-gray-300'}`}>{form.bio.length}/500</p>
          </div>

          {/* Refund Methods - only for non-creator users */}
          {user?.role !== 'creator' && (
          <div>
            <Label className="text-sm font-medium text-gray-500">Método de reembolso *</Label>
            <p className="text-xs text-gray-400 mt-1 mb-3">En caso de cancelación de una actividad, necesitamos saber a dónde enviarte tu reembolso. Llena al menos uno.</p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-400">CLABE interbancaria (18 dígitos)</Label>
                <Input
                  value={form.refund_clabe}
                  onChange={(e) => { const val = e.target.value.replace(/\D/g, '').slice(0, 18); setForm(prev => ({ ...prev, refund_clabe: val })); }}
                  placeholder="Ej: 012345678901234567"
                  inputMode="numeric"
                  className="mt-1"
                  data-testid="profile-refund-clabe"
                />
                {form.refund_clabe.length >= 3 && (() => {
                  const BANKS = {'002':'ABM','006':'Bancomext','009':'Banobras','012':'BBVA','014':'Santander','021':'HSBC','030':'Bajío','032':'IXE','036':'Inbursa','037':'Interacciones','042':'Mifel','044':'Scotiabank','058':'Banregio','059':'Invex','060':'Bansi','062':'Afirme','072':'Banorte','102':'ABN AMRO','103':'American Express','106':'BAMSA','108':'Tokyo','110':'JP Morgan','112':'Bansí','113':'CI Banco','116':'ING','124':'Deutsche','126':'Credit Suisse','127':'Azteca','128':'Autofin','129':'Barclays','130':'Compartamos','131':'Banco Famsa','132':'Multiva','133':'Actinver','134':'Intercam','136':'HSBC','137':'Bancoppel','138':'ABC Capital','139':'UBS','140':'Consubanco','141':'Volkswagen','143':'CIBanco','145':'Bbase','147':'Bankaool','148':'Pagatodo','150':'Inmobiliario','155':'ICBC','156':'Sabadell','157':'Shinhan','166':'BanCoppel'};
                  const code = form.refund_clabe.slice(0, 3);
                  const bank = BANKS[code];
                  return bank ? <p className="text-xs text-green-600 mt-1">Banco detectado: {bank}</p> : <p className="text-xs text-gray-400 mt-1">Banco no identificado</p>;
                })()}
              </div>
              {form.refund_clabe && (
              <div>
                <Label className="text-xs text-gray-400">Nombre del titular de la cuenta</Label>
                <Input
                  value={form.refund_clabe_holder}
                  onChange={(e) => setForm(prev => ({ ...prev, refund_clabe_holder: e.target.value }))}
                  placeholder="Nombre completo como aparece en el banco"
                  className="mt-1"
                  data-testid="profile-refund-holder"
                />
              </div>
              )}
              <div>
                <Label className="text-xs text-gray-400">Link de PayPal o Mercado Pago</Label>
                <Input
                  value={form.refund_paypal}
                  onChange={(e) => setForm(prev => ({ ...prev, refund_paypal: e.target.value }))}
                  placeholder="Ej: paypal.me/tu-usuario"
                  className="mt-1"
                  data-testid="profile-refund-paypal"
                />
              </div>
            </div>
          </div>
          )}

          {/* Push Notifications */}
          <div className="pt-2">
            <Label className="text-sm font-medium text-gray-500">Alertas</Label>
            <div className="mt-3 flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                {isSubscribed ? (
                  <Bell className="w-5 h-5 text-primary" />
                ) : (
                  <BellOff className="w-5 h-5 text-gray-400" />
                )}
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    {isSubscribed ? 'Notificaciones activadas' : 'Notificaciones en tu dispositivo'}
                  </p>
                  <p className="text-xs text-gray-400">
                    {permission === 'denied' 
                      ? 'Bloqueadas en tu navegador. Actívalas desde la configuración de tu navegador.' 
                      : isSubscribed 
                        ? 'Recibirás alertas en tu dispositivo aunque no tengas la página abierta'
                        : 'Recibe alertas de pagos, mensajes y más directamente en tu dispositivo'}
                  </p>
                </div>
              </div>
              <Button
                variant={isSubscribed ? "outline" : "default"}
                size="sm"
                className={`rounded-full text-xs ${isSubscribed ? '' : 'bg-primary hover:bg-primary-hover'}`}
                onClick={isSubscribed ? unsubscribe : subscribe}
                disabled={permission === 'denied'}
                data-testid="push-notification-toggle"
              >
                {isSubscribed ? 'Desactivar' : 'Activar'}
              </Button>
            </div>
          </div>

          {/* Social Links (for creators) */}
          {user?.role === 'creator' && (
            <div className="space-y-4">
              <Label className="text-sm font-medium text-gray-500">{t('profile.socialLinks')}</Label>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl border border-gray-200 flex items-center justify-center">
                    <Instagram className="w-5 h-5 text-gray-400" />
                  </div>
                  <Input
                    value={form.social_links.instagram}
                    onChange={(e) => setForm(prev => ({ ...prev, social_links: { ...prev.social_links, instagram: e.target.value } }))}
                    placeholder="https://instagram.com/tu_usuario"
                    className="flex-1 input-airbnb"
                    data-testid="profile-instagram-input"
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl border border-gray-200 flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor"><path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                  </div>
                  <Input
                    value={form.social_links.tiktok}
                    onChange={(e) => setForm(prev => ({ ...prev, social_links: { ...prev.social_links, tiktok: e.target.value } }))}
                    placeholder="https://tiktok.com/@tu_usuario"
                    className="flex-1 input-airbnb"
                    data-testid="profile-tiktok-input"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl border border-gray-200 flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  </div>
                  <Input
                    value={form.social_links.youtube}
                    onChange={(e) => setForm(prev => ({ ...prev, social_links: { ...prev.social_links, youtube: e.target.value } }))}
                    placeholder="https://youtube.com/@tu_canal"
                    className="flex-1 input-airbnb"
                    data-testid="profile-youtube-input"
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl border border-gray-200 flex items-center justify-center">
                    <Facebook className="w-5 h-5 text-gray-400" />
                  </div>
                  <Input
                    value={form.social_links.facebook}
                    onChange={(e) => setForm(prev => ({ ...prev, social_links: { ...prev.social_links, facebook: e.target.value } }))}
                    placeholder="https://facebook.com/tu_pagina"
                    className="flex-1 input-airbnb"
                    data-testid="profile-facebook-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Creator Gallery */}
          {user?.role === 'creator' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-gray-500">Galería</Label>
                <span className="text-xs text-gray-400">{galleryPhotos.length}/30</span>
              </div>
              <p className="text-xs text-gray-400 -mt-2">Agrega fotos de tus actividades pasadas para que los usuarios conozcan tu trabajo.</p>
              <div className="grid grid-cols-3 gap-2">
                {galleryPhotos.map((photo) => (
                  <div key={photo.photo_id} className="aspect-square rounded-xl overflow-hidden relative group">
                    <img src={photo.image_url} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => handleGalleryDelete(photo.photo_id)}
                      className="absolute top-1 right-1 p-1 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {galleryPhotos.length < 30 && (
                  <label className="aspect-square rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-all">
                    {uploadingGallery ? (
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <ImageIcon className="w-6 h-6 text-gray-300 mb-1" />
                        <span className="text-[10px] text-gray-400">Agregar</span>
                      </>
                    )}
                    <input type="file" accept="image/*" className="hidden" onChange={handleGalleryUpload} disabled={uploadingGallery} data-testid="gallery-upload-input" />
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Preview Profile Button */}
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/creator/${user?.slug || user?.user_id}`)}
            className="w-full rounded-xl py-5"
            data-testid="preview-profile-btn"
          >
            <Eye className="w-4 h-4 mr-2" />
            Ver mi perfil público
          </Button>

          {/* Save Button */}
          <Button 
            type="submit" 
            disabled={loading}
            className="w-full bg-primary hover:bg-primary-hover rounded-xl py-6 font-semibold"
            data-testid="save-profile-btn"
          >
            {loading ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </form>

        {/* Become Creator */}
        {user?.role !== 'creator' && (
          <div className="mt-10 pt-8 border-t border-gray-100">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <LayoutDashboard className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-rubik font-semibold text-lg mb-2">¿Quieres crear actividades?</h3>
              <p className="text-gray-400 mb-6">Conviértete en creador y comparte tus experiencias con la comunidad</p>
              <Button 
                onClick={handleBecomeCreator}
                variant="outline"
                className="rounded-xl px-8 py-3 h-auto"
                data-testid="become-creator-btn"
              >
                Convertirme en creador
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
